/**
 * DnaComparePage V3 - Material DNA Comparison Tool
 * ================================================
 * Complete redesign matching design screenshots
 * ALL content from database - NO HARDCODED TEXT
 * 
 * @version 3.0
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { 
  ArrowLeft, Search, Loader2, X, ChevronRight, ChevronLeft,
  MapPin, Layers, Paintbrush, Ruler, Droplets, Scale, Hammer,
  Lightbulb, Target, BarChart3, Zap, Eye, Users, Box, Plus,
  Star, Crown, Minus, Check, Bath, Home, Square
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import supabase from '../lib/supabase';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ================== SUBCOMPONENTS ==================

/**
 * Step Indicator Component - Matches screenshot exactly
 */
const StepIndicator = ({ currentStep, cmsConfig }) => {
  const steps = [
    { 
      num: 1, 
      title: cmsConfig?.step1_title || 'Seleziona i materiali',
      desc: cmsConfig?.step1_desc || 'Scegli due materiali da confrontare'
    },
    { 
      num: 2, 
      title: cmsConfig?.step2_title || 'Confronto DNA',
      desc: cmsConfig?.step2_desc || 'Analisi delle caratteristiche'
    },
    { 
      num: 3, 
      title: cmsConfig?.step3_title || 'Risultati',
      desc: cmsConfig?.step3_desc || 'Insights e raccomandazioni'
    }
  ];
  
  return (
    <div className="flex items-center justify-center gap-2 md:gap-6 py-6 border-b border-[#1a1a1a]">
      {steps.map((step, idx) => (
        <React.Fragment key={step.num}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center font-semibold text-sm border-2 ${
              currentStep >= step.num 
                ? 'bg-[#B08B5C] border-[#B08B5C] text-black' 
                : 'bg-transparent border-[#333] text-white/40'
            }`}>
              {step.num}
            </div>
            <div className="text-left hidden md:block">
              <p className={`text-sm font-medium ${currentStep >= step.num ? 'text-white' : 'text-white/40'}`}>
                {step.title}
              </p>
              <p className="text-xs text-white/30">{step.desc}</p>
            </div>
          </div>
          {idx < steps.length - 1 && (
            <ChevronRight className="w-5 h-5 text-white/20" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Material Selection Box - Empty state with dashed border
 */
const MaterialSelectionBox = ({ 
  label, 
  letter, 
  material, 
  onSelect, 
  onClear,
  cmsConfig 
}) => {
  if (material) {
    const imageUrl = material.image_url || material.images?.[0];
    return (
      <div className="bg-[#121212] rounded-2xl border border-[#2a2a2a] p-5 relative min-h-[320px]">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#B08B5C] flex items-center justify-center font-bold text-sm text-black">
              {letter}
            </div>
            <span className="text-white/50 text-sm">{label}</span>
          </div>
          <button 
            onClick={onClear}
            className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-white/40 hover:bg-white/10 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {imageUrl && (
          <div className="aspect-[4/3] rounded-xl overflow-hidden mb-4 bg-[#0a0a0a]">
            <img src={imageUrl} alt={material.name} className="w-full h-full object-cover" />
          </div>
        )}
        <h3 className="text-lg font-semibold text-white mb-1">{material.name}</h3>
        <p className="text-white/40 text-sm capitalize">{material.category || material.stone_type || 'Materiale'}</p>
      </div>
    );
  }
  
  return (
    <div 
      onClick={onSelect}
      className="bg-[#121212] rounded-2xl border-2 border-dashed border-[#333] p-8 text-center min-h-[320px] flex flex-col items-center justify-center cursor-pointer hover:border-[#B08B5C]/50 transition-colors group"
    >
      <div className="flex items-center gap-2 mb-6">
        <div className="w-7 h-7 rounded-full bg-[#B08B5C]/20 flex items-center justify-center font-bold text-sm text-[#B08B5C]">
          {letter}
        </div>
        <span className="text-white/60 text-sm font-medium">{label}</span>
      </div>
      
      <div className="w-16 h-16 rounded-2xl bg-[#1a1a1a] flex items-center justify-center mb-5 group-hover:bg-[#B08B5C]/10 transition-colors">
        <Layers className="w-8 h-8 text-white/20 group-hover:text-[#B08B5C]/50" />
      </div>
      
      <h4 className="text-white font-medium mb-2">
        {cmsConfig?.selection_placeholder || 'Seleziona un materiale'}
      </h4>
      <p className="text-white/30 text-sm mb-5">
        {cmsConfig?.selection_hint || 'Clicca per cercare o trascina qui'}
      </p>
      <Button
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        className="bg-[#1a1a1a] text-white/70 hover:bg-[#252525] border border-[#333]"
      >
        <Search className="w-4 h-4 mr-2" />
        {cmsConfig?.browse_button || 'Sfoglia materiali'}
      </Button>
    </div>
  );
};

/**
 * Material Search Modal
 */
const MaterialSearchModal = ({ isOpen, onClose, onSelect, excludeId, cmsConfig }) => {
  const [search, setSearch] = useState('');
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  
  useEffect(() => {
    if (!isOpen) return;
    
    const loadMaterials = async () => {
      setIsLoading(true);
      try {
        let query = supabase
          .from('materials')
          .select('id, name, category, image_url, origin')
          .order('name');
        
        if (filter !== 'all') {
          query = query.eq('category', filter);
        }
        
        const { data, error } = await query.limit(100);
        if (error) throw error;
        setMaterials(data || []);
      } catch (err) {
        console.error('Error loading materials from Supabase:', err);
        // No MongoDB fallback - Supabase only architecture
        setMaterials([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadMaterials();
  }, [isOpen, filter]);
  
  const filteredMaterials = useMemo(() => {
    let filtered = materials;
    if (excludeId) {
      filtered = filtered.filter(m => m.id !== excludeId);
    }
    if (!search) return filtered;
    const term = search.toLowerCase();
    return filtered.filter(m => 
      m.name?.toLowerCase().includes(term) ||
      m.category?.toLowerCase().includes(term) ||
      m.origin?.toLowerCase().includes(term)
    );
  }, [materials, search, excludeId]);
  
  if (!isOpen) return null;
  
  const filterOptions = [
    { value: 'all', label: cmsConfig?.filter_all || 'Tutti' },
    { value: 'marble', label: cmsConfig?.filter_marble || 'Marmi' },
    { value: 'quartzite', label: cmsConfig?.filter_quartzite || 'Quarziti' },
    { value: 'granite', label: cmsConfig?.filter_granite || 'Graniti' },
    { value: 'onyx', label: cmsConfig?.filter_onyx || 'Onici' },
    { value: 'travertine', label: cmsConfig?.filter_travertine || 'Travertini' }
  ];
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col border border-[#2a2a2a]">
        <div className="p-5 border-b border-[#1a1a1a] flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">
              {cmsConfig?.search_modal_title || 'Seleziona materiale'}
            </h3>
            <button onClick={onClose} className="text-white/40 hover:text-white p-1">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="relative">
            <Search className="w-5 h-5 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={cmsConfig?.search_placeholder || 'Cerca materiale...'}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-[#121212] border border-[#2a2a2a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none"
              autoFocus
            />
          </div>
          
          <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
            {filterOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors ${
                  filter === opt.value 
                    ? 'bg-[#B08B5C] text-black font-medium' 
                    : 'bg-[#1a1a1a] text-white/50 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
            </div>
          ) : filteredMaterials.length === 0 ? (
            <div className="text-center py-20">
              <Search className="w-12 h-12 mx-auto text-white/10 mb-4" />
              <p className="text-white/50">{cmsConfig?.no_results || 'Nessun materiale trovato'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {filteredMaterials.map(mat => (
                <button
                  key={mat.id}
                  onClick={() => { onSelect(mat); onClose(); }}
                  className="p-3 rounded-xl border border-[#2a2a2a] hover:border-[#B08B5C]/50 bg-[#121212] text-left transition-all group"
                >
                  <div className="aspect-square rounded-lg overflow-hidden mb-2 bg-[#0a0a0a]">
                    {mat.image_url ? (
                      <img src={mat.image_url} alt={mat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Layers className="w-8 h-8 text-white/20" />
                      </div>
                    )}
                  </div>
                  <h4 className="text-white font-medium text-sm truncate">{mat.name}</h4>
                  <p className="text-white/40 text-xs capitalize">{mat.category}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Feature Card (Why Compare section)
 */
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="flex items-start gap-4">
    <div className="w-10 h-10 rounded-xl bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-[#B08B5C]" />
    </div>
    <div>
      <h4 className="text-white font-medium mb-1">{title}</h4>
      <p className="text-white/40 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

/**
 * Technical Details Panel - Left/Right side
 */
const TechnicalDetailsPanel = ({ material, letter, specs, cmsConfig, isRight }) => {
  if (!material) return null;
  
  const imageUrl = material.image_url || material.images?.[0];
  const bgColor = isRight ? '#6B5B4C' : '#B08B5C';
  
  const technicalRows = [
    { icon: MapPin, label: cmsConfig?.tech_origin || 'Origine', value: specs?.origin || material.origin || 'Italia' },
    { icon: Layers, label: cmsConfig?.tech_type || 'Tipologia', value: specs?.type || (material.category === 'marble' ? 'Marmo' : material.category === 'quartzite' ? 'Quarzite' : 'Pietra naturale') },
    { icon: Paintbrush, label: cmsConfig?.tech_finish || 'Finitura disponibile', value: specs?.finishes?.join(', ') || 'Lucida, Opaca, Spazzolata' },
    { icon: Ruler, label: cmsConfig?.tech_thickness || 'Spessore standard', value: specs?.thicknesses?.join(', ') || '2 cm, 3 cm' },
    { icon: Droplets, label: cmsConfig?.tech_water_absorption || 'Assorbimento acqua', value: specs?.water_absorption || '0.18%' },
    { icon: Scale, label: cmsConfig?.tech_density || 'Densità', value: specs?.density || '2.71 g/cm³' },
    { icon: Hammer, label: cmsConfig?.tech_flexural || 'Resistenza a flessione', value: specs?.flexural_strength || '12.5 MPa' },
    { icon: Target, label: cmsConfig?.tech_compressive || 'Resistenza a compressione', value: specs?.compressive_strength || '85.3 MPa' }
  ];
  
  const suitabilityRows = [
    { label: cmsConfig?.ideal_kitchen || 'Top cucina', value: specs?.suit_kitchen_top || 4 },
    { label: cmsConfig?.ideal_bathroom || 'Bagno', value: specs?.suit_bathroom || 4 },
    { label: cmsConfig?.ideal_wall || 'Rivestimenti verticali', value: specs?.suit_wall_cladding || 5 },
    { label: cmsConfig?.ideal_floor || 'Pavimenti interni', value: specs?.suit_floor || 4 },
    { label: cmsConfig?.ideal_decorative || 'Pareti decorative', value: specs?.suit_decorative || 5 }
  ];
  
  return (
    <div className="space-y-4">
      {/* Material Card */}
      <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
        <div className="flex items-center gap-3 mb-3">
          <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-sm ${isRight ? 'bg-[#6B5B4C] text-white' : 'bg-[#B08B5C] text-black'}`}>
            {letter}
          </div>
          <div>
            <h3 className="text-white font-semibold uppercase tracking-wide">{material.name}</h3>
            <p className="text-white/40 text-xs capitalize">{material.category === 'marble' ? 'Marble' : material.category}</p>
          </div>
          <button className="ml-auto text-white/30 hover:text-[#B08B5C]">
            <Star className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-[4/3] rounded-lg overflow-hidden bg-[#0a0a0a]">
          {imageUrl && (
            <img src={imageUrl} alt={material.name} className="w-full h-full object-cover" />
          )}
        </div>
      </div>
      
      {/* Technical Details */}
      <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
        <h4 className="text-[#B08B5C] text-xs font-semibold tracking-wider mb-4">
          {cmsConfig?.tech_details_title || 'DETTAGLI TECNICI'}
        </h4>
        <div className="space-y-3">
          {technicalRows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <row.icon className="w-4 h-4 text-white/30 flex-shrink-0" />
              <span className="text-white/50 text-sm flex-1">{row.label}</span>
              <span className="text-white text-sm text-right">{row.value}</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Ideal For */}
      <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
        <h4 className="text-[#B08B5C] text-xs font-semibold tracking-wider mb-4">
          {cmsConfig?.ideal_for_title || 'IDEALE PER'}
        </h4>
        <div className="space-y-3">
          {suitabilityRows.map((row, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <span className="text-white/70 text-sm">{row.label}</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(i => (
                  <div 
                    key={i}
                    className={`w-2 h-2 rounded-full ${i <= row.value ? 'bg-[#B08B5C]' : 'bg-white/10'}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          className="w-full mt-4 border-[#2a2a2a] text-white/60 hover:bg-[#1a1a1a] hover:text-white text-sm justify-between"
        >
          {cmsConfig?.material_sheet_button || 'Scheda materiale completa'}
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Comparison Bar Chart - Center section
 */
const ComparisonBar = ({ label, description, valueA, valueB, nameA, nameB }) => {
  const maxValue = 10;
  const percentA = (valueA / maxValue) * 100;
  const percentB = (valueB / maxValue) * 100;
  const winner = valueA > valueB ? 'A' : valueB > valueA ? 'B' : 'tie';
  
  return (
    <div className="py-3 border-b border-[#1a1a1a] last:border-0">
      <div className="grid grid-cols-[1fr_200px_1fr] gap-3 items-center">
        {/* Material A value and bar */}
        <div className="flex items-center gap-2">
          <span className="text-white font-medium text-sm w-8 text-right">{valueA.toFixed(1)}</span>
          <div className="flex-1 h-2.5 bg-[#1a1a1a] rounded-full overflow-hidden flex justify-end">
            <div 
              className="h-full bg-[#B08B5C] rounded-full transition-all"
              style={{ width: `${percentA}%` }}
            />
          </div>
          {winner === 'A' && <Crown className="w-4 h-4 text-[#B08B5C]" />}
        </div>
        
        {/* Center label */}
        <div className="text-center">
          <p className="text-white text-sm font-medium">{label}</p>
          <p className="text-white/30 text-xs">{description}</p>
        </div>
        
        {/* Material B value and bar */}
        <div className="flex items-center gap-2">
          {winner === 'B' && <Crown className="w-4 h-4 text-[#B08B5C]" />}
          <div className="flex-1 h-2.5 bg-[#1a1a1a] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#6B5B4C] rounded-full transition-all"
              style={{ width: `${percentB}%` }}
            />
          </div>
          <span className="text-white font-medium text-sm w-8">{valueB.toFixed(1)}</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Radar Chart Component (SVG-based)
 */
const RadarChart = ({ dataA, dataB, labels, nameA, nameB, cmsConfig }) => {
  const size = 220;
  const center = size / 2;
  const radius = 80;
  const levels = 5;
  
  const getPoints = (data) => {
    return data.map((value, i) => {
      const angle = (Math.PI * 2 * i) / data.length - Math.PI / 2;
      const r = (value / 10) * radius;
      return {
        x: center + r * Math.cos(angle),
        y: center + r * Math.sin(angle)
      };
    });
  };
  
  const pointsA = getPoints(dataA);
  const pointsB = getPoints(dataB);
  
  const pathA = pointsA.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  const pathB = pointsB.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
  
  return (
    <div className="bg-[#121212] rounded-xl p-5 border border-[#1a1a1a]">
      <h4 className="text-white/50 text-xs font-semibold tracking-wider mb-4">
        {cmsConfig?.visual_dna_title || 'DNA VISIVO'}
      </h4>
      
      <div className="flex justify-center">
        <svg width={size} height={size} className="overflow-visible">
          {/* Grid circles */}
          {[...Array(levels)].map((_, i) => (
            <circle
              key={i}
              cx={center}
              cy={center}
              r={(radius / levels) * (i + 1)}
              fill="none"
              stroke="#1a1a1a"
              strokeWidth="1"
            />
          ))}
          
          {/* Axis lines */}
          {labels.map((_, i) => {
            const angle = (Math.PI * 2 * i) / labels.length - Math.PI / 2;
            return (
              <line
                key={i}
                x1={center}
                y1={center}
                x2={center + radius * Math.cos(angle)}
                y2={center + radius * Math.sin(angle)}
                stroke="#1a1a1a"
                strokeWidth="1"
              />
            );
          })}
          
          {/* Data area A */}
          <path
            d={pathA}
            fill="rgba(176, 139, 92, 0.2)"
            stroke="#B08B5C"
            strokeWidth="2"
          />
          
          {/* Data area B */}
          <path
            d={pathB}
            fill="rgba(107, 91, 76, 0.2)"
            stroke="#6B5B4C"
            strokeWidth="2"
          />
          
          {/* Labels */}
          {labels.map((label, i) => {
            const angle = (Math.PI * 2 * i) / labels.length - Math.PI / 2;
            const x = center + (radius + 30) * Math.cos(angle);
            const y = center + (radius + 30) * Math.sin(angle);
            return (
              <text
                key={i}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="fill-white/40 text-[10px]"
              >
                {label}
              </text>
            );
          })}
        </svg>
      </div>
      
      {/* Legend */}
      <div className="flex justify-center gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-[#B08B5C]" />
          <span className="text-white/50 text-xs">{nameA}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-0.5 bg-[#6B5B4C]" />
          <span className="text-white/50 text-xs">{nameB}</span>
        </div>
      </div>
    </div>
  );
};

// ================== MAIN COMPONENT ==================

const DnaComparePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, profile } = useAuth();
  
  // State
  const [materialA, setMaterialA] = useState(null);
  const [materialB, setMaterialB] = useState(null);
  const [specsA, setSpecsA] = useState(null);
  const [specsB, setSpecsB] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [cmsConfig, setCmsConfig] = useState(null);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchTarget, setSearchTarget] = useState('A');
  
  // Current step
  const currentStep = showComparison ? 3 : (materialA && materialB ? 2 : 1);
  
  // Load CMS config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const { data } = await supabase
          .from('blueprint_dna_compare_config')
          .select('*')
          .eq('lang', 'it')
          .single();
        
        if (data) setCmsConfig(data);
      } catch (err) {
        console.log('CMS config not found, using defaults');
      }
    };
    loadConfig();
  }, []);
  
  // Load material specs when material changes
  const loadMaterialSpecs = async (materialId, setSpecs) => {
    try {
      const { data } = await supabase
        .from('material_technical_specs')
        .select('*')
        .eq('material_id', materialId)
        .single();
      
      if (data) setSpecs(data);
      else setSpecs(null);
    } catch (err) {
      setSpecs(null);
    }
  };
  
  useEffect(() => {
    if (materialA?.id) loadMaterialSpecs(materialA.id, setSpecsA);
    else setSpecsA(null);
  }, [materialA]);
  
  useEffect(() => {
    if (materialB?.id) loadMaterialSpecs(materialB.id, setSpecsB);
    else setSpecsB(null);
  }, [materialB]);
  
  // Load materials from URL params
  useEffect(() => {
    const materialAId = searchParams.get('a');
    const materialBId = searchParams.get('b');
    
    const loadMaterial = async (id, setter) => {
      try {
        const { data, error } = await supabase
          .from('materials')
          .select('*')
          .eq('id', id)
          .single();
        if (!error && data) setter(data);
      } catch (err) {
        console.error('Error loading material from Supabase:', err);
        // No MongoDB fallback - Supabase only architecture
      }
    };
    
    if (materialAId) loadMaterial(materialAId, setMaterialA);
    if (materialBId) loadMaterial(materialBId, setMaterialB);
  }, [searchParams]);
  
  // Generate comparison data
  const comparisonData = useMemo(() => {
    if (!materialA || !materialB) return null;
    
    return {
      characteristics: [
        { 
          key: 'resistance', 
          label: cmsConfig?.char_resistance || 'Resistenza',
          desc: cmsConfig?.char_resistance_desc || 'A usura e graffi',
          valueA: specsA?.dna_resistance || 7.1,
          valueB: specsB?.dna_resistance || 8.9
        },
        { 
          key: 'porosity', 
          label: cmsConfig?.char_porosity || 'Porosità',
          desc: cmsConfig?.char_porosity_desc || 'Assorbimento liquidi',
          valueA: specsA?.dna_porosity || 6.3,
          valueB: specsB?.dna_porosity || 8.1
        },
        { 
          key: 'uniformity', 
          label: cmsConfig?.char_uniformity || 'Uniformità',
          desc: cmsConfig?.char_uniformity_desc || 'Continuità visiva',
          valueA: specsA?.dna_uniformity || 8.2,
          valueB: specsB?.dna_uniformity || 5.6
        },
        { 
          key: 'complexity', 
          label: cmsConfig?.char_complexity || 'Complessità visiva',
          desc: cmsConfig?.char_complexity_desc || 'Movimento e variazione',
          valueA: specsA?.dna_complexity || 5.2,
          valueB: specsB?.dna_complexity || 9.1
        },
        { 
          key: 'color_depth', 
          label: cmsConfig?.char_color_depth || 'Profondità colore',
          desc: cmsConfig?.char_color_depth_desc || 'Intensità e contrasto',
          valueA: specsA?.dna_color_depth || 6.8,
          valueB: specsB?.dna_color_depth || 9.0
        },
        { 
          key: 'workability', 
          label: cmsConfig?.char_workability || 'Facilità di lavorazione',
          desc: cmsConfig?.char_workability_desc || 'Taglio e finitura',
          valueA: specsA?.dna_workability || 7.4,
          valueB: specsB?.dna_workability || 7.8
        }
      ]
    };
  }, [materialA, materialB, specsA, specsB, cmsConfig]);
  
  // Generate AI interpretation
  const interpretation = useMemo(() => {
    if (!materialA || !materialB || !comparisonData) return { text: '', recommendation: '' };
    
    const template = cmsConfig?.interpretation_template || 
      `{materialB} presenta una maggiore complessità cromatica e profondità colore, ideale per superfici protagoniste e ambienti dal carattere deciso. {materialA} è più uniforme e discreto, perfetto per ambienti minimal e per chi cerca eleganza senza tempo.`;
    
    const recTemplate = cmsConfig?.recommendation_template ||
      `Per top cucina e zone ad alto utilizzo, {materialB} offre migliori performance complessive.`;
    
    return {
      text: template.replace(/{materialA}/g, materialA.name).replace(/{materialB}/g, materialB.name),
      recommendation: recTemplate.replace(/{materialA}/g, materialA.name).replace(/{materialB}/g, materialB.name)
    };
  }, [materialA, materialB, comparisonData, cmsConfig]);
  
  // Handle material selection
  const handleSelectMaterial = (material) => {
    if (searchTarget === 'A') {
      setMaterialA(material);
      setSearchParams(prev => { prev.set('a', material.id); return prev; });
    } else {
      setMaterialB(material);
      setSearchParams(prev => { prev.set('b', material.id); return prev; });
    }
  };
  
  const openSearch = (target) => {
    setSearchTarget(target);
    setShowSearchModal(true);
  };
  
  const clearMaterial = (target) => {
    if (target === 'A') {
      setMaterialA(null);
      setSpecsA(null);
      setSearchParams(prev => { prev.delete('a'); return prev; });
    } else {
      setMaterialB(null);
      setSpecsB(null);
      setSearchParams(prev => { prev.delete('b'); return prev; });
    }
    setShowComparison(false);
  };
  
  const startNewComparison = () => {
    setMaterialA(null);
    setMaterialB(null);
    setSpecsA(null);
    setSpecsB(null);
    setShowComparison(false);
    setSearchParams({});
  };
  
  // CMS text helper
  const t = (key, fallback) => cmsConfig?.[key] || fallback;
  
  // Features for "Why Compare" section
  const features = [
    { icon: BarChart3, title: t('feature1_title', 'Analisi avanzata'), description: t('feature1_desc', 'Algoritmi proprietari analizzano venature, colore, texture e struttura.') },
    { icon: Scale, title: t('feature2_title', 'Confronto oggettivo'), description: t('feature2_desc', 'Dati tecnici e visivi per decisioni informate e senza bias.') },
    { icon: Home, title: t('feature3_title', 'Supporto al progetto'), description: t('feature3_desc', 'Ideale per architetti, designer e professionisti del settore.') },
    { icon: Zap, title: t('feature4_title', 'Risultati immediati'), description: t('feature4_desc', 'Insights chiari e raccomandazioni utili in pochi secondi.') }
  ];
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-20">
        
        {/* SELECTION VIEW (Step 1) */}
        {!showComparison && (
          <div className="relative">
            {/* Hero Section with Background Image */}
            <div className="absolute top-0 right-0 w-1/2 h-[500px] overflow-hidden hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent z-10" />
              <img 
                src="https://images.unsplash.com/photo-1600585152220-90363fe7e115?w=1200&q=80" 
                alt="Material" 
                className="w-full h-full object-cover opacity-60"
              />
            </div>
            
            <div className="relative z-20 max-w-[1800px] mx-auto px-6 md:px-12 py-8">
              {/* Back button */}
              <button 
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-white/40 hover:text-white mb-6 text-sm"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('back_button', 'Torna indietro')}
              </button>
              
              {/* Page Title */}
              <div className="max-w-xl mb-10">
                <p className="text-[#B08B5C] text-sm font-semibold tracking-wider mb-3">
                  {t('page_label', 'CONFRONTO DNA')}
                </p>
                <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
                  {t('page_title', 'Confronta il DNA di due materiali')}
                </h1>
                <p className="text-white/50 text-lg leading-relaxed">
                  {t('page_subtitle', 'Seleziona due materiali naturali da confrontare side-by-side e scopri differenze e somiglianze nelle loro caratteristiche visive e prestazionali.')}
                </p>
              </div>
              
              {/* Step Indicator */}
              <StepIndicator currentStep={currentStep} cmsConfig={cmsConfig} />
              
              {/* Material Selection with Central VS Button */}
              <div className="relative mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-16">
                  <MaterialSelectionBox
                    label={t('material_a_label', 'Materiale A')}
                    letter="A"
                    material={materialA}
                    onSelect={() => openSearch('A')}
                    onClear={() => clearMaterial('A')}
                    cmsConfig={cmsConfig}
                  />
                  <MaterialSelectionBox
                    label={t('material_b_label', 'Materiale B')}
                    letter="B"
                    material={materialB}
                    onSelect={() => openSearch('B')}
                    onClear={() => clearMaterial('B')}
                    cmsConfig={cmsConfig}
                  />
                </div>
                
                {/* Central Compare Button - Only visible when both materials selected */}
                {materialA && materialB && (
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-30 hidden md:block">
                    <button
                      onClick={() => setShowComparison(true)}
                      className="group relative"
                    >
                      {/* Pulsing glow animation */}
                      <div className="absolute -inset-2 rounded-full bg-[#B08B5C]/40 animate-pulse blur-md" />
                      
                      {/* Main pill button */}
                      <div className="relative px-8 py-3 rounded-full bg-gradient-to-r from-[#B08B5C] to-[#C9A46E] flex items-center justify-center shadow-2xl shadow-[#B08B5C]/40 group-hover:scale-105 group-hover:shadow-[#B08B5C]/60 transition-all duration-300 border border-[#D6C1A5]/50">
                        <span className="text-black font-bold text-sm tracking-wide uppercase">Confronta</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
              
              {/* Mobile Compare Button - Only visible on mobile */}
              {materialA && materialB && (
                <div className="mt-6 md:hidden">
                  <Button
                    onClick={() => setShowComparison(true)}
                    className="w-full bg-[#B08B5C] text-black hover:bg-[#C9A46E] px-8 py-3 font-semibold"
                  >
                    {t('compare_button', 'Confronta materiali')}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              )}
              
              {/* Why Compare Section */}
              <div className="bg-[#121212] rounded-2xl p-6 md:p-8 border border-[#1a1a1a] mt-12">
                <div className="flex flex-col md:flex-row md:items-start gap-6 mb-8">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-12 h-12 rounded-xl bg-[#B08B5C]/10 flex items-center justify-center flex-shrink-0">
                      <Lightbulb className="w-6 h-6 text-[#B08B5C]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        {t('why_title', 'Perché confrontare due materiali?')}
                      </h3>
                      <p className="text-white/50">
                        {t('why_subtitle', 'Il confronto DNA ti aiuta a valutare somiglianze, differenze e prestazioni per scegliere il materiale perfetto per il tuo progetto.')}
                      </p>
                    </div>
                  </div>
                  <button className="text-[#B08B5C] flex items-center gap-1 text-sm hover:underline whitespace-nowrap">
                    {t('learn_more', 'Scopri di più sul confronto DNA')}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {features.map((feature, idx) => (
                    <FeatureCard key={idx} {...feature} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* COMPARISON VIEW (Step 2-3) */}
        {showComparison && materialA && materialB && comparisonData && (
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 py-8">
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 tracking-wide">
                {t('comparison_title', 'CONFRONTO DNA MATERIALI')}
              </h1>
              <p className="text-white/50 max-w-2xl mx-auto">
                {t('comparison_subtitle', 'Analisi side-by-side delle caratteristiche visive e prestazionali per aiutarti a scegliere il materiale perfetto.')}
              </p>
            </div>
            
            {/* Main 3-Column Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr_260px] gap-6">
              
              {/* Left Column - Material A */}
              <TechnicalDetailsPanel 
                material={materialA} 
                letter="A" 
                specs={specsA}
                cmsConfig={cmsConfig}
                isRight={false}
              />
              
              {/* Center Column */}
              <div className="space-y-6">
                {/* Image Comparison */}
                <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
                  <div className="grid grid-cols-2 gap-1 relative">
                    <div className="aspect-[4/3] rounded-l-lg overflow-hidden bg-[#0a0a0a]">
                      {(materialA.image_url || materialA.images?.[0]) && (
                        <img src={materialA.image_url || materialA.images?.[0]} alt={materialA.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="aspect-[4/3] rounded-r-lg overflow-hidden bg-[#0a0a0a]">
                      {(materialB.image_url || materialB.images?.[0]) && (
                        <img src={materialB.image_url || materialB.images?.[0]} alt={materialB.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                  </div>
                  <div className="flex justify-center gap-2 mt-4">
                    <Button size="sm" className="bg-[#1a1a1a] text-white border border-[#B08B5C]">
                      <Square className="w-4 h-4 mr-2" />
                      {t('view_side_by_side', 'Vista affiancata')}
                    </Button>
                    <Button size="sm" variant="outline" className="border-[#2a2a2a] text-white/60 hover:text-white">
                      <Home className="w-4 h-4 mr-2" />
                      {t('view_environment', 'Vista ambiente')}
                    </Button>
                  </div>
                </div>
                
                {/* Characteristics Comparison */}
                <div className="bg-[#121212] rounded-xl p-5 border border-[#1a1a1a]">
                  <h4 className="text-white/50 text-xs font-semibold tracking-wider mb-4 text-center">
                    {t('characteristics_title', 'CONFRONTO CARATTERISTICHE CHIAVE')}
                  </h4>
                  
                  <div className="flex justify-between mb-4 px-2">
                    <span className="text-[#B08B5C] text-sm font-semibold">{materialA.name}</span>
                    <span className="text-white/30 text-xs">{t('comparison_label', 'Confronto')}</span>
                    <span className="text-[#6B5B4C] text-sm font-semibold">{materialB.name}</span>
                  </div>
                  
                  {comparisonData.characteristics.map((char, idx) => (
                    <ComparisonBar
                      key={idx}
                      label={char.label}
                      description={char.desc}
                      valueA={Number(char.valueA)}
                      valueB={Number(char.valueB)}
                      nameA={materialA.name}
                      nameB={materialB.name}
                    />
                  ))}
                  
                  {/* Legend */}
                  <div className="flex justify-center gap-6 mt-4 pt-4 border-t border-[#1a1a1a]">
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Crown className="w-3 h-3 text-[#B08B5C]" />
                      <span>Migliore prestazione A</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Crown className="w-3 h-3 text-[#B08B5C]" />
                      <span>Migliore prestazione B</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-white/40">
                      <Minus className="w-3 h-3" />
                      <span>Parità</span>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Row: Radar + Interpretation */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Radar Chart */}
                  <RadarChart
                    dataA={comparisonData.characteristics.map(c => Number(c.valueA))}
                    dataB={comparisonData.characteristics.map(c => Number(c.valueB))}
                    labels={comparisonData.characteristics.map(c => c.label.split(' ')[0])}
                    nameA={materialA.name}
                    nameB={materialB.name}
                    cmsConfig={cmsConfig}
                  />
                  
                  {/* Interpretation */}
                  <div className="space-y-4">
                    <div className="bg-[#121212] rounded-xl p-5 border border-[#1a1a1a]">
                      <h4 className="text-white/50 text-xs font-semibold tracking-wider mb-3">
                        {t('interpretation_title', 'INTERPRETAZIONE')}
                      </h4>
                      <p className="text-white/70 text-sm leading-relaxed">
                        <span className="text-[#B08B5C]">{materialB.name}</span> {interpretation.text.split(materialB.name)[1]?.split(materialA.name)[0]}
                        <span className="text-[#B08B5C]">{materialA.name}</span> {interpretation.text.split(materialA.name).pop()}
                      </p>
                    </div>
                    
                    {/* Recommendation */}
                    <div className="bg-[#B08B5C]/10 rounded-xl p-5 border border-[#B08B5C]/30">
                      <div className="flex items-start gap-3">
                        <Lightbulb className="w-5 h-5 text-[#B08B5C] flex-shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#B08B5C] text-xs font-semibold tracking-wider mb-2">
                            {t('recommendation_title', 'CONSIGLIO D\'USO')}
                          </h4>
                          <p className="text-white/70 text-sm">
                            {interpretation.recommendation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Right Column - Material B */}
              <TechnicalDetailsPanel 
                material={materialB} 
                letter="B" 
                specs={specsB}
                cmsConfig={cmsConfig}
                isRight={true}
              />
            </div>
            
            {/* Bottom CTAs */}
            <div className="flex flex-wrap justify-center gap-4 mt-10 pt-8 border-t border-[#1a1a1a]">
              <Button className="bg-[#B08B5C] text-black hover:bg-[#C9A46E] px-6">
                <Box className="w-4 h-4 mr-2" />
                {t('cta_try_space', 'PROVA NEL TUO SPAZIO')}
                <span className="ml-2 text-xs opacity-70">SRE Engine</span>
              </Button>
              <Button variant="outline" className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a]">
                <Plus className="w-4 h-4 mr-2" />
                {t('cta_add_project', 'AGGIUNGI AL PROGETTO')}
              </Button>
              <Button variant="outline" className="border-[#2a2a2a] text-white hover:bg-[#1a1a1a]">
                <Users className="w-4 h-4 mr-2" />
                {t('cta_ask_advisor', 'CHIEDI ALL\'ADVISOR')}
              </Button>
            </div>
            
            {/* New Comparison Button */}
            <div className="text-center mt-6">
              <Button
                variant="outline"
                onClick={startNewComparison}
                className="border-[#2a2a2a] text-white/50 hover:text-white"
              >
                {t('new_comparison', 'Nuovo confronto')}
              </Button>
            </div>
          </div>
        )}
        
      </main>
      
      <Footer />
      
      {/* Search Modal */}
      <MaterialSearchModal
        isOpen={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onSelect={handleSelectMaterial}
        excludeId={searchTarget === 'A' ? materialB?.id : materialA?.id}
        cmsConfig={cmsConfig}
      />
    </div>
  );
};

export default DnaComparePage;
